
import { useState } from "react";
import { voteForPrompt, submitPhoto, voteForMatchup } from "../../lib/api";
import { useToast } from "@/components/ui/use-toast";
import { Challenge as ChallengeType, Prompt } from "../../lib/types";
import { User } from "../../lib/types";

export function useChallengeHandlers({
  user,
  challenge,
  setChallenge,
  setPromptVotes,
  setVotedPromptId,
  setHasSubmitted,
  setHill,
  setChallengers,
  setCurrentChallenger,
  hill,
  challengers,
  currentChallenger,
  setIsVoting,
  setCurrentMatchup,
}: any) {
  const { toast } = useToast();

  // Vote for prompt, with optimistic update
  const handleVotePrompt = async (promptId: string) => {
    if (!user) return;
    setVotedPromptId(promptId);
    setPromptVotes((prev: any) => ({
      ...prev,
      [promptId]: (prev[promptId] || 0) + 1,
    }));
    try {
      const updatedChallenge = await voteForPrompt(promptId, user.id);
      setChallenge(updatedChallenge);
      if (updatedChallenge.promptOptions) {
        const newVotes: { [id: string]: number } = {};
        updatedChallenge.promptOptions.forEach((p) => {
          newVotes[p.id] = p.votes;
        });
        setPromptVotes(newVotes);
      }
      toast({
        title: "Vote successful",
        description: "Your vote has been recorded!",
      });
    } catch (err) {
      setPromptVotes((prev: any) => ({
        ...prev,
        [promptId]: Math.max((prev[promptId] || 1) - 1, 0),
      }));
      setVotedPromptId(null);
      toast({
        title: "Error",
        description: "Could not register your vote. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle photo capture and submission
  const handlePhotoCapture = async (photoUrl: string) => {
    if (!user) return;
    try {
      const updatedChallenge = await submitPhoto(user.id, user.username, photoUrl);
      setChallenge(updatedChallenge);
      setHasSubmitted(true);
      toast({
        title: "Submission successful",
        description: "Your photo has been submitted!",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Could not submit your photo. Please try again.",
        variant: "destructive",
      });
    }
  };

  // King of the Hill vote handler
  const handleHillVote = async (winnerId: string) => {
    if (!user || !hill || !currentChallenger) return;
    setIsVoting(true);

    let nextHill, nextChallengers;
    if (winnerId === hill.id) {
      nextHill = hill;
      nextChallengers = challengers.slice(1);
    } else if (winnerId === currentChallenger.id) {
      nextHill = currentChallenger;
      nextChallengers = [hill, ...challengers.slice(1)];
    } else {
      nextHill = hill;
      nextChallengers = challengers.slice(1);
    }

    try {
      await voteForMatchup(user.id, 'kingofthehill', winnerId);
      setHill(nextHill);
      setChallengers(nextChallengers);
      setCurrentChallenger(nextChallengers[0] || null);

      if (nextChallengers.length === 0) {
        toast({
          title: "Voting complete!",
          description: "You've seen all submissions. Thank you for playing king of the hill!"
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Could not record your vote.",
        variant: "destructive"
      });
    } finally {
      setIsVoting(false);
    }
  };

  // Handle A/B Vote
  const handleABVote = async (winnerId: string, currentMatchup: any) => {
    if (!user || !currentMatchup) return;
    try {
      setIsVoting(true);
      const { nextMatchup } = await voteForMatchup(user.id, currentMatchup.id, winnerId);
      if (nextMatchup) {
        setCurrentMatchup(nextMatchup);
      } else {
        toast({
          title: "Thank you!",
          description: "Your votes have been recorded.",
        });
        setCurrentMatchup(null);
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Could not record your vote.",
        variant: "destructive",
      });
    } finally {
      setIsVoting(false);
    }
  };

  return {
    handleVotePrompt,
    handlePhotoCapture,
    handleHillVote,
    handleABVote,
  };
}
